import { reservationKind } from '../../shared/autoPublish';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import styled from 'styled-components';
import { Modal } from '../../shared/components/Modal';
import { Button } from '../../shared/components/Button';
import { Field, Input, Textarea } from '../../shared/components/Field';
import { Alert, Spinner } from '../../shared/components/feedback';
import { ApiError } from '../../api/client';
import * as threadReservationApi from '../../api/threadReservation';
import * as productApi from '../../api/product';
import type { ProductImage } from '../../api/product';
import type { ThreadAccountPostReservation } from '../../api/types';
import {
  fromDateAndTime,
  pickWallClock,
  toDateTimeLocalParts,
} from '../../shared/datetimeWallClock';

type Props = {
  open: boolean;
  task: ThreadAccountPostReservation | null;
  onClose: () => void;
  onSaved: (task: ThreadAccountPostReservation) => void;
  onPublished: () => void;
};

function pickRandomImage(
  images: ProductImage[],
  excludeId?: number | null
): ProductImage | null {
  if (images.length === 0) return null;
  const pool =
    images.length > 1 && excludeId != null
      ? images.filter((img) => img.id !== excludeId)
      : images;
  return pool[Math.floor(Math.random() * pool.length)] ?? null;
}

type DraftMedia = {
  url: string;
  type: string;
  followUp: boolean;
  productImageId?: number | null;
};

const MAX_ROOT_MEDIA = 10;

function parseDraftMedia(
  json?: string | null,
  fallbackUrl?: string | null
): DraftMedia[] {
  if (json) {
    try {
      const parsed = JSON.parse(json) as DraftMedia[];
      if (Array.isArray(parsed)) {
        return parsed.filter((m) => m?.url);
      }
    } catch {
      /* ignore */
    }
  }
  if (fallbackUrl?.trim()) {
    return [{ url: fallbackUrl.trim(), type: 'IMAGE', followUp: false }];
  }
  return [];
}

export function ReservationDraftModal({
  open,
  task,
  onClose,
  onSaved,
  onPublished,
}: Props) {
  const [draftPost, setDraftPost] = useState('');
  const [draftImageUrl, setDraftImageUrl] = useState('');
  const [mediaItems, setMediaItems] = useState<DraftMedia[]>([]);
  const [allowTextOnly, setAllowTextOnly] = useState(false);
  const [topicTag, setTopicTag] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const followUpInputRef = useRef<HTMLInputElement>(null);

  const reloadProductImages = async (productId: number) => {
    try {
      const images = await productApi.listProductImages(productId);
      setProductImages(images);
    } catch {
      setProductImages([]);
    }
  };

  // 모달을 열 때만 서버 값 로드 (저장 응답으로 시각을 덮지 않음)
  useEffect(() => {
    if (!open || !task) return;
    setDraftPost(task.draftPost ?? '');
    setDraftImageUrl(task.draftImageUrl ?? '');
    setMediaItems(parseDraftMedia(task.draftMediaJson, task.draftImageUrl));
    setAllowTextOnly(false);
    setTopicTag(task.topicTag ?? '');
    const parts = toDateTimeLocalParts(pickWallClock(task));
    setScheduleDate(parts.date);
    setScheduleTime(parts.time);
    setError(null);
    // task 객체 전체가 바뀌어도 같은 예약이면 입력값을 덮지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  useEffect(() => {
    if (!open || !task?.productId) {
      setProductImages([]);
      return;
    }
    let cancelled = false;
    productApi
      .listProductImages(task.productId)
      .then((images: ProductImage[]) => {
        if (!cancelled) setProductImages(images);
      })
      .catch(() => {
        if (!cancelled) setProductImages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, task?.productId]);

  if (!task) return null;

  const buildDraftPayload = () => {
    const wall = fromDateAndTime(scheduleDate, scheduleTime);
    const clearTopic = !topicTag.trim();
    const roots = mediaItems.filter((m) => !m.followUp);
    const firstRoot = roots[0];
    const firstProductId =
      roots.find((m) => m.productImageId != null)?.productImageId ?? null;
    return {
      draftPost: draftPost.trim(),
      allowTextOnly,
      draftImageUrl: firstRoot?.url ?? '',
      draftMediaJson: JSON.stringify(mediaItems),
      topicTag: clearTopic ? null : topicTag.trim(),
      clearTopicTag: clearTopic,
      scheduledAt: wall,
      scheduled_at: wall,
      clearScheduledAt: wall == null,
      productImageId: firstProductId,
    };
  };

  const handleSave = async () => {
    if (!draftPost.trim()) {
      setError('본문을 입력해 주세요.');
      return;
    }
    if ((scheduleDate && !scheduleTime) || (!scheduleDate && scheduleTime)) {
      setError('예약 날짜와 시간을 모두 입력하거나, 둘 다 비워 주세요.');
      return;
    }
    setSaving(true);
    setError(null);
    const wall = fromDateAndTime(scheduleDate, scheduleTime);
    try {
      const payload = buildDraftPayload();
      const updated = await threadReservationApi.updateReservationDraft(
        task.id,
        payload
      );
      onSaved({
        ...updated,
        scheduledAt: wall,
      });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.description || err.message : '초안 저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    if (!draftPost.trim()) {
      setError('본문을 입력해 주세요.');
      return;
    }
    if ((scheduleDate && !scheduleTime) || (!scheduleDate && scheduleTime)) {
      setError('예약 날짜와 시간을 모두 입력하거나, 둘 다 비워 주세요.');
      return;
    }
    setPublishing(true);
    setError(null);
    const wall = fromDateAndTime(scheduleDate, scheduleTime);
    try {
      const payload = buildDraftPayload();
      let updated = await threadReservationApi.updateReservationDraft(
        task.id,
        payload
      );
      onSaved({ ...updated, scheduledAt: wall });
      if (updated.source === 'NOTION' && !updated.notionAiRewrittenAt) {
        updated = await threadReservationApi.prepareReservation(task.id);
        onSaved(updated);
        setDraftPost(updated.draftPost || '');
        if (updated.status === 'AWAITING_APPROVAL') {
          setError('AI 제작이 완료되었습니다. 최종 글을 확인한 뒤 승인해 주세요.');
          return;
        }
      }
      if (updated.status === 'AWAITING_APPROVAL') {
        const approved = await threadReservationApi.approveReservation(task.id);
        onSaved(approved);
      }
      await threadReservationApi.publishReservation(task.id);
      onPublished();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.description || err.message : '발송에 실패했습니다.'
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleApprove = async () => {
    if (!draftPost.trim() || (scheduleDate && !scheduleTime) || (!scheduleDate && scheduleTime)) {
      setError('본문과 예약 날짜·시간을 확인해 주세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await threadReservationApi.updateReservationDraft(task.id, buildDraftPayload());
      const approved = updated.status === 'AWAITING_APPROVAL'
        ? await threadReservationApi.approveReservation(task.id) : updated;
      onSaved(approved);
    } catch (err) {
      setError(err instanceof Error ? err.message : '초안을 승인하지 못했습니다.');
    } finally { setSaving(false); }
  };

  const rootMedia = mediaItems.filter((m) => !m.followUp);
  const followUpMedia = mediaItems.filter((m) => m.followUp);

  const isProductImageSelected = (img: ProductImage) =>
    mediaItems.some(
      (m) =>
        !m.followUp &&
        (m.productImageId === img.id || m.url === img.publicUrl)
    );

  const syncDraftImageUrl = (items: DraftMedia[]) => {
    setDraftImageUrl(items.find((m) => !m.followUp)?.url ?? '');
  };

  const toggleProductImage = (img: ProductImage) => {
    if (isProductImageSelected(img)) {
      const next = mediaItems.filter(
        (m) =>
          m.followUp ||
          (m.productImageId !== img.id && m.url !== img.publicUrl)
      );
      setMediaItems(next);
      syncDraftImageUrl(next);
      return;
    }
    if (rootMedia.length >= MAX_ROOT_MEDIA) {
      setError(`한 글에 이미지는 최대 ${MAX_ROOT_MEDIA}장입니다.`);
      return;
    }
    const next = [
      ...mediaItems.filter((m) => !m.followUp),
      {
        url: img.publicUrl,
        type: 'IMAGE',
        followUp: false,
        productImageId: img.id,
      },
      ...mediaItems.filter((m) => m.followUp),
    ];
    setMediaItems(next);
    syncDraftImageUrl(next);
    setError(null);
  };

  const removeMedia = (url: string, followUp: boolean) => {
    const next = mediaItems.filter(
      (m) => !(m.url === url && m.followUp === followUp)
    );
    setMediaItems(next);
    syncDraftImageUrl(next);
  };

  const clearImage = () => {
    setDraftImageUrl('');
    setMediaItems(mediaItems.filter((m) => m.followUp));
  };

  const reshuffleImage = () => {
    if (rootMedia.length >= MAX_ROOT_MEDIA) {
      setError(`한 글에 이미지는 최대 ${MAX_ROOT_MEDIA}장입니다.`);
      return;
    }
    const selectedIds = new Set(
      mediaItems
        .map((m) => m.productImageId)
        .filter((id): id is number => id != null)
    );
    const unused = productImages.filter(
      (img) =>
        !selectedIds.has(img.id) &&
        !mediaItems.some((m) => !m.followUp && m.url === img.publicUrl)
    );
    const next = pickRandomImage(unused);
    if (next) toggleProductImage(next);
  };

  const handleUploadImage = async (
    fileList: FileList | null,
    followUp = false
  ) => {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      let items = mediaItems;
      for (const file of files) {
        const rootCount = items.filter((m) => !m.followUp).length;
        if (!followUp && rootCount >= MAX_ROOT_MEDIA) {
          setError(`한 글에 이미지는 최대 ${MAX_ROOT_MEDIA}장입니다.`);
          break;
        }
        const updated = await threadReservationApi.uploadReservationDraftImage(
          task.id,
          file,
          followUp
        );
        const parsed = parseDraftMedia(
          updated.draftMediaJson,
          updated.draftImageUrl
        );
        const group = parsed.filter((m) => m.followUp === followUp);
        const added = group[group.length - 1];
        if (added && !items.some((m) => m.url === added.url)) {
          items = followUp
            ? [...items, added]
            : [
                ...items.filter((m) => !m.followUp),
                added,
                ...items.filter((m) => m.followUp),
              ];
        }
        onSaved(updated);
        if (updated.productId != null) {
          await reloadProductImages(updated.productId);
        }
      }
      setMediaItems(items);
      syncDraftImageUrl(items);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.description || err.message : '미디어 업로드에 실패했습니다.'
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (followUpInputRef.current) followUpInputRef.current.value = '';
    }
  };

  return (
    <Modal open={open} onClose={saving || publishing || uploading ? undefined : onClose} width={640} labelledBy="draft-title">
      <Body>
        <Header>
          <Title id="draft-title">예약 초안 · {reservationKind(task)}</Title>
          {task.source === 'NOTION' && <Alert tone="info">
            {task.notionAiRewrittenAt ? '계정의 말투 설정으로 AI 제작이 완료된 글입니다. 발행 재시도 시 이 글을 재사용합니다.'
              : '노션 원문입니다. 발행 직전에 계정의 말투 설정으로 AI가 재작성합니다. 승인 계정은 제작 후 최종 글을 확인해야 합니다.'}
          </Alert>}
          <Subject>{task.subject}</Subject>
        </Header>

        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="본문" htmlFor="draftPost">
          <Textarea
            id="draftPost"
            aria-describedby="draft-character-count"
            disabled={saving || publishing || uploading}
            value={draftPost}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setDraftPost(e.target.value)
            }
            rows={8}
            maxLength={500}
          />
          <CharCount id="draft-character-count" $limit={draftPost.length >= 500}>{draftPost.length}/500자{draftPost.length >= 500 ? ' · 최대 글자 수에 도달했습니다' : ''}</CharCount>
        </Field>

        <Field label="커뮤니티 (선택)" htmlFor="topicTag" hint="비워 두면 커뮤니티 없이 발행">
          <Input
            id="topicTag"
            disabled={saving || publishing || uploading}
            value={topicTag}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setTopicTag(e.target.value)
            }
            placeholder="없으면 비워 두세요"
          />
        </Field>

        <Field label="발송 예약 시각" htmlFor="scheduleDate" hint="한국 시간">
          <ScheduleRow>
            <Input
              id="scheduleDate"
              type="date"
              aria-label="발송 예약 날짜"
              aria-describedby="draft-schedule-help"
              disabled={saving || publishing || uploading}
              value={scheduleDate}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setScheduleDate(e.target.value)
              }
            />
            <Input
              id="scheduleTime"
              type="time"
              aria-label="발송 예약 시간"
              aria-describedby="draft-schedule-help"
              disabled={saving || publishing || uploading}
              value={scheduleTime}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setScheduleTime(e.target.value)
              }
            />
          </ScheduleRow>
          <Help id="draft-schedule-help">
            날짜와 시간을 모두 선택하세요. 이 유형의 자동 발행이 켜져 있어야 예약 시각에 발송됩니다. 비워 두면 지금 발송만 가능합니다.
          </Help>
        </Field>

        <ImageSection>
          <LabelRow>
            <Label>
              사진 · 영상
              {rootMedia.length > 0 ? ` (${rootMedia.length}/${MAX_ROOT_MEDIA})` : ''}
            </Label>
            <ImageActions>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={uploading || saving || publishing}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? <Spinner /> : '추가'}
              </Button>
              {productImages.length > 0 && (
                <Button
                  type="button"
                  $variant="ghost"
                  $size="sm"
                  disabled={saving || publishing || uploading || rootMedia.length >= MAX_ROOT_MEDIA}
                  onClick={reshuffleImage}
                >
                  랜덤 한 장
                </Button>
              )}
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={saving || publishing || uploading || rootMedia.length === 0}
                onClick={clearImage}
              >
                지우기
              </Button>
            </ImageActions>
          </LabelRow>
          {task.includeImage && !mediaItems.some((item) => !item.followUp) && (
            <label style={{ display: 'block', margin: '12px 0', fontSize: 13 }}>
              <input type="checkbox" checked={allowTextOnly} disabled={saving || publishing}
                onChange={(e) => setAllowTextOnly(e.target.checked)} /> 이미지 없이 발행
              <span style={{ display: 'block', marginTop: 6 }}>이미지가 필요한 예약입니다. 이미지를 첨부하거나 이 항목을 선택해야 발행할 수 있습니다.</span>
            </label>
          )}
          <HiddenFile
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/quicktime"
            multiple
            onChange={(e) => void handleUploadImage(e.target.files, false)}
          />
          <Help>
            소재 사진을 여러 장 고르거나, 기기에서 여러 장을 한 번에 올릴 수
            있습니다. 2장 이상이면 캐러셀로 발행됩니다. (최대 {MAX_ROOT_MEDIA}장)
          </Help>
          {rootMedia.length > 0 && (
            <MediaStrip>
              {rootMedia.map((m, index) => (
                <MediaThumb key={m.url}>
                  {m.type === 'VIDEO' ? (
                    <span>VIDEO</span>
                  ) : (
                    <img src={m.url} alt="" />
                  )}
                  <RemoveThumb
                    type="button"
                    aria-label={`${index + 1}번째 미디어 제거`}
                    disabled={saving || publishing || uploading}
                    onClick={() => removeMedia(m.url, false)}
                  >
                    ×
                  </RemoveThumb>
                </MediaThumb>
              ))}
            </MediaStrip>
          )}
          {task.productId != null && productImages.length > 0 && (
            <ImageGrid>
              {productImages.map((img) => (
                <ImagePick
                  key={img.id}
                  type="button"
                  $active={isProductImageSelected(img)}
                  aria-pressed={isProductImageSelected(img)}
                  aria-label={`${img.fileName} 선택`}
                  disabled={saving || publishing || uploading}
                  onClick={() => toggleProductImage(img)}
                >
                  <img src={img.publicUrl} alt={img.fileName} />
                </ImagePick>
              ))}
            </ImageGrid>
          )}
        </ImageSection>

        <details>
          <Summary>후속 스레드 미디어 (선택)</Summary>
          <Help>
            본문 다음에 이어 붙는 사진·영상입니다.
          </Help>
          <HiddenFile
            ref={followUpInputRef}
            type="file"
            accept="image/*,video/mp4,video/quicktime"
            multiple
            onChange={(e) => void handleUploadImage(e.target.files, true)}
          />
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={uploading || saving || publishing}
            onClick={() => followUpInputRef.current?.click()}
          >
            후속 추가
          </Button>
          {followUpMedia.length > 0 && (
            <MediaStrip>
              {followUpMedia.map((m, index) => (
                  <MediaThumb key={`fu-${m.url}`}>
                    {m.type === 'VIDEO' ? (
                      <span>VIDEO</span>
                    ) : (
                      <img src={m.url} alt="" />
                    )}
                    <RemoveThumb
                      type="button"
                      aria-label={`${index + 1}번째 후속 미디어 제거`}
                      disabled={saving || publishing || uploading}
                      onClick={() => removeMedia(m.url, true)}
                    >
                      ×
                    </RemoveThumb>
                  </MediaThumb>
                ))}
            </MediaStrip>
          )}
        </details>

        {draftImageUrl.trim() && mediaItems.length === 0 && (
          <Preview>
            <img src={draftImageUrl.trim()} alt="미리보기" />
          </Preview>
        )}

        <Actions>
          <Button type="button" $variant="ghost" disabled={saving || publishing || uploading} onClick={onClose}>
            닫기
          </Button>
          {task.status === 'AWAITING_APPROVAL' && (
            <Button type="button" $variant="secondary" disabled={saving || publishing || uploading || !draftPost.trim()} onClick={() => void handleApprove()}>
              승인하고 예약 유지
            </Button>
          )}
          <Button
            type="button"
            $variant="secondary"
            disabled={saving || publishing || uploading}
            onClick={() => void handleSave()}
          >
            {saving ? <><Spinner /> 저장 중…</> : '초안 저장'}
          </Button>
          <Button
            type="button"
            disabled={saving || publishing || uploading || !draftPost.trim()}
            onClick={() => void handlePublishNow()}
          >
            {publishing ? <><Spinner /> 처리 중…</> : task.source === 'NOTION' && !task.notionAiRewrittenAt ? (task.approvalRequired ? 'AI 제작 후 검토' : 'AI 제작 후 지금 발송') : task.approvalRequired || task.status === 'AWAITING_APPROVAL' ? '승인하고 지금 발송' : '지금 발송'}
          </Button>
        </Actions>
      </Body>
    </Modal>
  );
}

const Body = styled.div`
  padding: 24px;
  @media (max-width: 520px) { padding: 20px 16px; }
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

const Subject = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CharCount = styled.span<{ $limit: boolean }>`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme, $limit }) => $limit ? theme.colors.danger : theme.colors.textSecondary};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const Help = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
`;

const Summary = styled.summary`
  min-height: 44px;
  padding: 12px 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const ScheduleRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 10px;
  input { min-width: 0; }
  @media (max-width: 380px) { grid-template-columns: 1fr; }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const ImageActions = styled.div`
  display: flex;
  gap: 4px;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 8px;
`;

const MediaStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MediaThumb = styled.div`
  position: relative;
  width: 92px;
  height: 92px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSecondary};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveThumb = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  font-size: 22px;
  line-height: 44px;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
`;

const ImagePick = styled.button<{ $active?: boolean }>`
  position: relative;
  ${({ $active, theme }) => $active && `&::after { content: '✓'; position: absolute; right: 4px; bottom: 4px; width: 24px; height: 24px; display: grid; place-items: center; border-radius: 50%; color: ${theme.colors.onPrimary}; background: ${theme.colors.primary}; font-weight: 700; }`}
  &:disabled { opacity: 0.65; cursor: not-allowed; }
  padding: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 2px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border};
  overflow: hidden;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surfaceMuted};

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
  }
`;

const HiddenFile = styled.input`
  display: none;
`;

const Preview = styled.div`
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  max-height: 200px;

  img {
    display: block;
    width: 100%;
    max-height: 200px;
    object-fit: contain;
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }
`;

const Actions = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin: 4px -24px -24px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  @media (max-width: 520px) {
    bottom: 0;
    margin: 4px -16px -20px;
    padding: 12px 16px;
    button { flex: 1 1 auto; }
  }
`;
